import "@/lib/sentry";

import { createRootRoute, Outlet } from "@tanstack/react-router";
import { useCallback, useMemo, useState } from "react";

import { ThemeContext } from "@/hooks/useTheme";
import { Sentry } from "@/lib/sentry";

export const Route = createRootRoute({
  component: RootComponent,
});

function ErrorFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-light font-sans">
      <div className="text-center">
        <h1 className="mb-4 font-heading text-4xl text-black">
          Something went wrong
        </h1>
        <p className="mb-8 text-black/60">
          An unexpected error occurred. Please try again.
        </p>
        <button
          onClick={() => window.location.reload()}
          className="rounded-xl border border-black bg-brand-coral px-6 py-3 font-bold text-white transition-colors hover:bg-brand-coral-dark"
        >
          Reload page
        </button>
      </div>
    </div>
  );
}

function RootComponent() {
  const [isDark, setIsDark] = useState(() => {
    try {
      return localStorage.getItem("theme") === "dark";
    } catch {
      return false;
    }
  });
  const [forceLightMode, setForceLightMode] = useState(false);
  const toggleTheme = useCallback(() => {
    setIsDark((d) => {
      const next = !d;
      try { localStorage.setItem("theme", next ? "dark" : "light"); } catch {}
      return next;
    });
  }, []);
  const setForceLight = useCallback((force: boolean) => setForceLightMode(force), []);

  const effectiveDark = isDark && !forceLightMode;
  const ctx = useMemo(() => ({ isDark: effectiveDark, toggleTheme, setForceLightMode: setForceLight }), [effectiveDark, toggleTheme, setForceLight]);

  return (
    <ThemeContext.Provider value={ctx}>
      <div className={effectiveDark ? "dark" : ""}>
        <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
          <Outlet />
        </Sentry.ErrorBoundary>
      </div>
    </ThemeContext.Provider>
  );
}
